import { useEffect, useMemo, useState, type FormEvent } from 'react'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { FilePicker, Textarea, TextInput } from '../Common'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { SheetSelect } from '../Common/SheetSelect'
import {
  addFieldPassTaskComment,
  getFieldPassSession,
  recordFieldPassScan,
  redeemFieldPass,
  sendFieldPassMessage,
  updateFieldPassTaskStatus,
  uploadFieldPassProof,
} from '../../services/fieldPassService'
import {
  submitFieldPassRoll,
  submitFieldPassExamSeats,
  submitFieldPassSessionMetrics,
} from '../../services/eduFieldPassSchoolService'
import { RollCallRow, type RollCallStatus } from '../Common/RollCallRow'
import { RollSummaryBar, type RollSummaryCounts } from '../Common/RollSummaryBar'
import type { ChecklistWorkflowStatus } from '../../types/schema'
import type { FieldPassRecord, FieldPassSessionSnapshot, FieldPassTask } from '../../types/fieldPass'
import { NotFoundPage } from '../NotFoundPage'
import { useNls } from '../../nls/nlsProvider'

// Firebase Callable error codes the backend uses to signal an invalid Field
// Pass token (see functions/src/fieldPass.ts redeemFieldPass). Any of these
// collapses the whole surface to a 404 so a URL guesser can't distinguish
// "wrong token" from "route doesn't exist". Other errors (validation,
// network) stay inline so a real user hitting a hiccup can retry.
const INVALID_PASS_CODES = new Set([
  'functions/not-found',
  'functions/permission-denied',
  'functions/deadline-exceeded',
])

const isInvalidPassError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const code = (error as { code?: unknown }).code
  return typeof code === 'string' && INVALID_PASS_CODES.has(code)
}

type TabKey = 'TODOS' | 'CHAT' | 'CONTROLS'

const STATUS_OPTIONS: ChecklistWorkflowStatus[] = ['ACCEPTED', 'ASSESSING', 'ONGOING', 'COMPLETED', 'POSTPONED', 'NOT_REQUIRED']

const statusLabel = (status: ChecklistWorkflowStatus) => ({
  CREATED: 'New',
  ASSIGNED: 'Assigned',
  ASSESSING: 'Assessing',
  ACCEPTED: 'Accepted',
  ONGOING: 'In progress',
  COMPLETED: 'Completed',
  NOT_REQUIRED: 'Not required',
  POSTPONED: 'Postponed',
})[status]

const formatTime = (value: unknown): string => {
  if (!value || typeof value !== 'object' || typeof (value as { toDate?: unknown }).toDate !== 'function') return ''
  try {
    return (value as { toDate: () => Date }).toDate().toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  } catch {
    return ''
  }
}

const formatEndTime = (value: unknown): string => {
  if (!value) return ''
  try {
    let date: Date | null = null
    if (typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
      date = (value as { toDate: () => Date }).toDate()
    } else if (value instanceof Date) {
      date = value
    } else if (typeof value === 'string' || typeof value === 'number') {
      date = new Date(value)
    }
    if (date && !isNaN(date.getTime())) {
      return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    }
  } catch {
    // ignore
  }
  return ''
}

const isPassExpired = (pass?: FieldPassRecord | null): boolean => {
  if (!pass) return false
  if (pass.status === 'EXPIRED') return true
  const exp = pass.expiresAt
  if (!exp) return false
  try {
    let ms: number | null = null
    const expObj = exp as unknown as { toMillis?: () => number; toDate?: () => Date }
    if (typeof expObj.toMillis === 'function') {
      ms = expObj.toMillis()
    } else if (typeof expObj.toDate === 'function') {
      ms = expObj.toDate().getTime()
    } else if (typeof exp === 'string' || typeof exp === 'number') {
      ms = new Date(exp).getTime()
    }
    return Boolean(ms && Date.now() > ms)
  } catch {
    return false
  }
}

const taskDate = (task: FieldPassTask) => {
  if (!task.scheduleStartDate && !task.scheduleEndDate) return 'No date set'
  return [task.scheduleStartDate, task.scheduleEndDate].filter(Boolean).join(' - ')
}

export const FieldPassLanding = ({ token }: { token: string }) => {
  const { t } = useNls()
  const [displayName, setDisplayName] = useState('')
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [passId, setPassId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return window.localStorage.getItem(`fieldPass:${token}:passId`)
  })
  const [snapshot, setSnapshot] = useState<FieldPassSessionSnapshot | null>(null)
  const [tab, setTab] = useState<TabKey>('TODOS')
  const [chatDraft, setChatDraft] = useState('')
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({})
  const [scanDraft, setScanDraft] = useState('')
  const [scanTaskId, setScanTaskId] = useState<string>('')
  const [scanMode, setScanMode] = useState<'IN' | 'OUT'>('OUT')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [tokenIsInvalid, setTokenIsInvalid] = useState(false)

  const activeChannel = snapshot?.channels[0] ?? null
  const messages = activeChannel ? snapshot?.messagesByChannel[activeChannel.id] ?? [] : []

  const refresh = async (id = passId) => {
    if (!id) return
    const next = await getFieldPassSession(id)
    setSnapshot(next)
    if (!scanTaskId && next.tasks[0]) setScanTaskId(next.tasks[0].id)
  }

  useEffect(() => {
    if (!passId) return
    const onError = (caught: unknown) => {
      if (isInvalidPassError(caught)) {
        setTokenIsInvalid(true)
        return
      }
      setError(caught instanceof Error ? caught.message : 'Field Pass could not be loaded.')
    }
    void refresh(passId).catch(onError)
    const timer = window.setInterval(() => {
      void refresh(passId).catch(onError)
    }, 10_000)
    return () => window.clearInterval(timer)
  }, [passId])

  const redeem = async (event: FormEvent) => {
    event.preventDefault()
    if (!displayName.trim()) {
      setError('Display name / first name is required.')
      return
    }
    setBusy('redeem')
    setError(null)
    try {
      const result = await redeemFieldPass({
        token,
        displayName: displayName.trim(),
        fullName: fullName.trim() || null,
        phone: phone.trim() || null,
      })
      setPassId(result.passId)
      window.localStorage.setItem(`fieldPass:${token}:passId`, result.passId)
      setStatus('Field Pass opened.')
      const next = await getFieldPassSession(result.passId)
      setSnapshot(next)
      if (next.tasks[0]) setScanTaskId(next.tasks[0].id)
    } catch (caught) {
      if (isInvalidPassError(caught)) {
        setTokenIsInvalid(true)
      } else {
        setError(caught instanceof Error ? caught.message : 'Could not open this Field Pass.')
      }
    } finally {
      setBusy(null)
    }
  }

  const sendChat = async (event: FormEvent) => {
    event.preventDefault()
    if (!passId || !chatDraft.trim()) return
    setBusy('chat')
    setError(null)
    try {
      setSnapshot(await sendFieldPassMessage(passId, chatDraft.trim()))
      setChatDraft('')
      setTab('CHAT')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not send message.')
    } finally {
      setBusy(null)
    }
  }

  const updateStatus = async (taskId: string, workflowStatus: ChecklistWorkflowStatus) => {
    if (!passId) return
    setBusy(`status:${taskId}`)
    setError(null)
    try {
      setSnapshot(await updateFieldPassTaskStatus(passId, taskId, workflowStatus))
      setStatus('Status updated.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not update status.')
    } finally {
      setBusy(null)
    }
  }

  const addComment = async (taskId: string) => {
    if (!passId || !commentDrafts[taskId]?.trim()) return
    setBusy(`comment:${taskId}`)
    setError(null)
    try {
      setSnapshot(await addFieldPassTaskComment(passId, taskId, commentDrafts[taskId].trim()))
      setCommentDrafts((current) => ({ ...current, [taskId]: '' }))
      setStatus('Comment added.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not add comment.')
    } finally {
      setBusy(null)
    }
  }

  const submitScan = async (event: FormEvent) => {
    event.preventDefault()
    if (!passId || !scanDraft.trim()) return
    setBusy('scan')
    setError(null)
    try {
      setSnapshot(await recordFieldPassScan(passId, scanTaskId || null, scanDraft.trim(), scanMode))
      setScanDraft('')
      setStatus('Scan recorded.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not record scan.')
    } finally {
      setBusy(null)
    }
  }

  const uploadProof = async (taskId: string, file: File | null | undefined) => {
    if (!passId || !file) return
    setBusy(`proof:${taskId}`)
    setError(null)
    try {
      setSnapshot(await uploadFieldPassProof(passId, taskId, file))
      setStatus('Proof uploaded.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not upload proof.')
    } finally {
      setBusy(null)
    }
  }

  const completionCount = useMemo(() => {
    const tasks = snapshot?.tasks ?? []
    return tasks.filter((task) => task.workflowStatus === 'COMPLETED' || task.isCompleted).length
  }, [snapshot?.tasks])

  // --- School Mode State (EM10.b) ---
  const isSchoolPass = Boolean(snapshot?.pass.eduScope)
  const eduScope = snapshot?.pass.eduScope
  const isExpired = isPassExpired(snapshot?.pass)
  const endTimeStr = formatEndTime(snapshot?.pass.expiresAt ?? snapshot?.session.expiresAt)

  const learners = useMemo(() => {
    return snapshot?.learners ?? []
  }, [snapshot?.learners])

  const [eduMarks, setEduMarks] = useState<Record<string, RollCallStatus>>(() => {
    if (!passId || typeof window === 'undefined') return {}
    try {
      const raw = window.localStorage.getItem(`fieldPass:${passId}:eduDraft`)
      if (raw) return JSON.parse(raw).marks ?? {}
    } catch {
      // ignore
    }
    return {}
  })
  const [eduSeats, setEduSeats] = useState<Record<string, { seatNo: string; present: boolean }>>(() => {
    if (!passId || typeof window === 'undefined') return {}
    try {
      const raw = window.localStorage.getItem(`fieldPass:${passId}:eduDraft`)
      if (raw) return JSON.parse(raw).seats ?? {}
    } catch {
      // ignore
    }
    return {}
  })
  const [eduMetrics, setEduMetrics] = useState<Record<string, { value: number | string; notes: string }>>(() => {
    if (!passId || typeof window === 'undefined') return {}
    try {
      const raw = window.localStorage.getItem(`fieldPass:${passId}:eduDraft`)
      if (raw) return JSON.parse(raw).metrics ?? {}
    } catch {
      // ignore
    }
    return {}
  })
  const [eduSubmitted, setEduSubmitted] = useState<boolean>(() => Boolean(snapshot?.pass.submittedAt))
  const [eduSubmitBusy, setEduSubmitBusy] = useState(false)

  // Restore draft on pass load
  useEffect(() => {
    if (!passId || !isSchoolPass) return
    const timer = window.setTimeout(() => {
      try {
        const raw = window.localStorage.getItem(`fieldPass:${passId}:eduDraft`)
        if (raw) {
          const draft = JSON.parse(raw)
          if (draft.marks) setEduMarks(draft.marks)
          if (draft.seats) setEduSeats(draft.seats)
          if (draft.metrics) setEduMetrics(draft.metrics)
        }
      } catch {
        // ignore
      }
    }, 0)
    return () => window.clearTimeout(timer)
  }, [passId, isSchoolPass])

  // Save drafts to localStorage
  const saveEduDraft = (draft: {
    marks?: Record<string, RollCallStatus>
    seats?: Record<string, { seatNo: string; present: boolean }>
    metrics?: Record<string, { value: number | string; notes: string }>
  }) => {
    if (!passId || typeof window === 'undefined') return
    try {
      const existingRaw = window.localStorage.getItem(`fieldPass:${passId}:eduDraft`)
      const existing = existingRaw ? JSON.parse(existingRaw) : {}
      window.localStorage.setItem(`fieldPass:${passId}:eduDraft`, JSON.stringify({ ...existing, ...draft }))
    } catch {
      // ignore
    }
  }

  const handleSessionMarkChange = (learnerId: string, nextStatus: RollCallStatus) => {
    setEduMarks((prev) => {
      const next = { ...prev, [learnerId]: nextStatus }
      saveEduDraft({ marks: next })
      return next
    })
  }

  const handleExamSeatChange = (learnerId: string, seatNo: string) => {
    setEduSeats((prev) => {
      const cur = prev[learnerId] ?? { seatNo: '', present: true }
      const next = { ...prev, [learnerId]: { ...cur, seatNo } }
      saveEduDraft({ seats: next })
      return next
    })
  }

  const handleExamPresentToggle = (learnerId: string) => {
    setEduSeats((prev) => {
      const cur = prev[learnerId] ?? { seatNo: '', present: true }
      const next = { ...prev, [learnerId]: { ...cur, present: !cur.present } }
      saveEduDraft({ seats: next })
      return next
    })
  }

  const handleCoachMetricChange = (learnerId: string, metricDefId: string, value: string) => {
    const key = `${learnerId}_${metricDefId}`
    setEduMetrics((prev) => {
      const cur = prev[key] ?? { value: '', notes: '' }
      const next = { ...prev, [key]: { ...cur, value } }
      saveEduDraft({ metrics: next })
      return next
    })
  }

  const handleCoachNotesChange = (learnerId: string, notes: string) => {
    const key = `${learnerId}_notes`
    setEduMetrics((prev) => {
      const cur = prev[key] ?? { value: '', notes: '' }
      const next = { ...prev, [key]: { ...cur, notes } }
      saveEduDraft({ metrics: next })
      return next
    })
  }

  const clearEduDraft = () => {
    if (!passId || typeof window === 'undefined') return
    try {
      window.localStorage.removeItem(`fieldPass:${passId}:eduDraft`)
    } catch {
      // ignore
    }
  }

  // Submit handlers
  const submitSessionRoll = async () => {
    if (!passId || isExpired || eduSubmitBusy) return
    setEduSubmitBusy(true)
    setError(null)
    try {
      const marks = learners.map((l) => {
        const status = eduMarks[l.learnerId] ?? (l.status === 'ON_LEAVE' ? 'OL' : 'P')
        return {
          learnerId: l.learnerId,
          status: (status === 'P' ? 'PRESENT' : status === 'A' ? 'ABSENT' : status === 'L' ? 'LATE' : 'ON_LEAVE') as 'PRESENT' | 'ABSENT' | 'LATE' | 'ON_LEAVE',
        }
      })
      await submitFieldPassRoll({ passId, marks })
      setEduSubmitted(true)
      clearEduDraft()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('NLSFieldPassSchoolErrorSubmitRoll'))
    } finally {
      setEduSubmitBusy(false)
    }
  }

  const submitExamSeats = async () => {
    if (!passId || isExpired || eduSubmitBusy) return
    setEduSubmitBusy(true)
    setError(null)
    try {
      const seats = learners.map((l, idx) => ({
        learnerId: l.learnerId,
        seatNo: eduSeats[l.learnerId]?.seatNo ?? String(idx + 1),
      }))
      await submitFieldPassExamSeats({ passId, seats })
      setEduSubmitted(true)
      clearEduDraft()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('NLSFieldPassSchoolErrorSubmitExamSeats'))
    } finally {
      setEduSubmitBusy(false)
    }
  }

  const submitCoachMetrics = async () => {
    if (!passId || isExpired || eduSubmitBusy) return
    setEduSubmitBusy(true)
    setError(null)
    try {
      const metricDefIds = eduScope?.metricDefIds ?? ['score']
      const metrics: Array<{ learnerId: string; metricDefId: string; value: number | string; notes?: string }> = []
      for (const l of learners) {
        const notes = eduMetrics[`${l.learnerId}_notes`]?.notes
        for (const mId of metricDefIds) {
          const val = eduMetrics[`${l.learnerId}_${mId}`]?.value ?? 0
          metrics.push({
            learnerId: l.learnerId,
            metricDefId: mId,
            value: val,
            notes,
          })
        }
      }
      await submitFieldPassSessionMetrics({ passId, metrics })
      setEduSubmitted(true)
      clearEduDraft()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('NLSFieldPassSchoolErrorSubmitSessionMetrics'))
    } finally {
      setEduSubmitBusy(false)
    }
  }

  const sessionRollCounts = useMemo<RollSummaryCounts>(() => {
    let present = 0
    let absent = 0
    let late = 0
    let onLeave = 0
    for (const status of Object.values(eduMarks)) {
      if (status === 'P') present++
      else if (status === 'A') absent++
      else if (status === 'L') late++
      else if (status === 'OL') onLeave++
    }
    return {
      present,
      absent,
      late,
      onLeave,
      total: learners.length,
    }
  }, [eduMarks, learners.length])

  if (tokenIsInvalid) {
    return <NotFoundPage path={typeof window !== 'undefined' ? window.location.pathname : `/field-pass/${token}`} />
  }

  if (!passId || !snapshot) {
    return (
      <main className="min-h-dvh bg-[var(--surface)] px-4 py-6 text-[var(--text-strong)]">
        <section
          className="mx-auto flex min-h-[calc(100dvh-3rem)] max-w-md flex-col justify-center"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <p className="text-[11px] uppercase tracking-[0.24em] text-[var(--accent-strong)]">Field Pass</p>
          <h1 className="mt-3 text-3xl font-semibold">Temporary field access</h1>
          <p className="mt-2 text-sm text-[var(--muted)]">Open your assigned tasks, chat with the member who sent this pass, and submit field updates from this browser.</p>
          <form id="fieldPassRedeemForm" onSubmit={redeem} className="mt-6 grid gap-3 rounded-2xl border border-[var(--border)] bg-[var(--surface-card)] p-4">
            <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-[var(--muted)]">
              Display name / first name *
              <TextInput value={displayName} onChange={setDisplayName} />
            </label>
            <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-[var(--muted)]">
              Full name
              <TextInput value={fullName} onChange={setFullName} />
            </label>
            <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-[var(--muted)]">
              Phone
              <TextInput type="tel" value={phone} onChange={setPhone} />
            </label>
            {error ? <p className="rounded-lg border border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] px-3 py-2 text-xs text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]">{error}</p> : null}
          </form>
          <div
            className="sticky bottom-0 mt-4 -mx-4 border-t border-[var(--border)] bg-[var(--surface)]/95 px-4 py-3 backdrop-blur"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
          >
            <Button variant="primary" type="submit" form="fieldPassRedeemForm" disabled={busy === 'redeem'} className="w-full">
              Open Field Pass
            </Button>
          </div>
        </section>
      </main>
    )
  }

  return (
    <main className="min-h-dvh bg-[var(--surface)] text-[var(--text-strong)]">
      <header
        className="sticky top-0 z-10 border-b border-[var(--border)] bg-[var(--surface)]/95 px-4 py-3 backdrop-blur"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      >
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent-strong)]">Field Pass</p>
            <h1 className="text-lg font-semibold break-words">{snapshot.session.displayName}</h1>
            {endTimeStr ? (
              <p className="text-xs text-[var(--muted)]" data-testid="field-pass-ends-at">
                {t('NLSFieldPassSchoolEndsAt', { time: endTimeStr })}
              </p>
            ) : null}
          </div>
          <span className="rounded-full border border-[color-mix(in_srgb,var(--warning)_40%,transparent)] bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]">Burner</span>
        </div>
      </header>

      {isSchoolPass ? (
        <section className="mx-auto grid max-w-3xl gap-4 px-4 py-4 pb-24" data-testid="field-pass-school-section">
          {error ? (
            <p className="rounded-xl border border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] px-3 py-2 text-xs text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]">
              {error}
            </p>
          ) : null}

          {isExpired ? (
            <div
              className="rounded-2xl border border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] p-4 text-center text-sm font-semibold text-[color-mix(in_srgb,var(--danger)_80%,var(--text-strong))]"
              data-testid="field-pass-expired-banner"
            >
              {t('NLSFieldPassSchoolExpired')}
            </div>
          ) : null}

          {eduSubmitted || Boolean(snapshot.pass.submittedAt) ? (
            <div
              className="rounded-2xl border border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[color-mix(in_srgb,var(--success)_10%,transparent)] p-4 text-center text-sm font-semibold text-[color-mix(in_srgb,var(--success)_80%,var(--text-strong))]"
              data-testid="field-pass-submitted-notice"
            >
              {t('NLSFieldPassSchoolSubmittedNotice')}
            </div>
          ) : null}

          {!eduSubmitted && !snapshot.pass.submittedAt && !isExpired ? (
            <>
              {eduScope?.kind === 'SESSION' ? (
                <div className="space-y-4" data-testid="field-pass-session-mode">
                  <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-card)] p-4">
                    <h2 className="text-base font-semibold">{snapshot.pass.workspaceName}</h2>
                    <p className="text-xs text-[var(--muted)]">{t('NLSFieldPassSchoolHeadingSession')}</p>
                  </div>

                  <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] divide-y divide-[var(--border-subtle)] overflow-hidden" data-testid="school-pass-session-roll">
                    {learners.map((learner) => (
                      <RollCallRow
                        key={learner.learnerId}
                        rollNumber={learner.rollNo ?? undefined}
                        name={learner.displayName}
                        status={eduMarks[learner.learnerId] ?? (learner.status === 'ON_LEAVE' ? 'OL' : 'P')}
                        locked={Boolean(eduMarks[learner.learnerId] === 'OL' || (!eduMarks[learner.learnerId] && learner.status === 'ON_LEAVE'))}
                        onChange={(next) => handleSessionMarkChange(learner.learnerId, next)}
                        testId={`session-roll-${learner.learnerId}`}
                      />
                    ))}
                    {learners.length === 0 ? (
                      <div className="p-4 text-center text-xs text-[var(--muted)]">{t('NLSFieldPassSchoolEmptySession')}</div>
                    ) : null}
                  </div>

                  <RollSummaryBar
                    counts={sessionRollCounts}
                    actionLabel={eduSubmitBusy ? t('NLSFieldPassSchoolSubmitting') : t('NLSFieldPassSchoolSubmitRoll')}
                    onAction={submitSessionRoll}
                    busy={eduSubmitBusy}
                    disabled={isExpired}
                    testId="session-roll-summary"
                  />
                </div>
              ) : null}

              {eduScope?.kind === 'EXAM' ? (
                <div className="space-y-4" data-testid="field-pass-exam-mode">
                  <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-card)] p-4">
                    <h2 className="text-base font-semibold">{snapshot.pass.workspaceName}</h2>
                    <p className="text-xs text-[var(--muted)]">
                      {eduScope.hall ? t('NLSFieldPassSchoolHeadingExamWithHall', { hall: eduScope.hall }) : t('NLSFieldPassSchoolHeadingExam')}
                    </p>
                  </div>

                  <div className="space-y-2" data-testid="school-pass-exam-seats">
                    {learners.map((l, idx) => (
                      <div
                        key={l.learnerId}
                        className="flex items-center justify-between rounded-xl border border-[var(--border)] bg-[var(--surface-card)] p-3 min-h-[44px]"
                        data-testid={`exam-seat-row-${l.learnerId}`}
                      >
                        <div>
                          <p className="text-sm font-semibold">{l.displayName}</p>
                          <p className="text-xs text-[var(--muted)]">{t('NLSFieldPassSchoolRollNumber', { rollNo: l.rollNo ?? '—' })}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <TextInput
                            aria-label={t('NLSFieldPassSchoolAriaSeatFor', { name: l.displayName })}
                            value={eduSeats[l.learnerId]?.seatNo ?? String(idx + 1)}
                            onChange={(val) => handleExamSeatChange(l.learnerId, val)}
                            className="w-16 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 py-1 text-center text-xs"
                            placeholder={t('NLSFieldPassSchoolPlaceholderSeat')}
                            data-testid={`exam-seat-input-${l.learnerId}`}
                          />
                          <Pressable
                            type="button"
                            onClick={() => handleExamPresentToggle(l.learnerId)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                              eduSeats[l.learnerId]?.present !== false
                                ? 'bg-[var(--accent)] text-[var(--accent-contrast)]'
                                : 'border border-[var(--border)] bg-[var(--surface)] text-[var(--text)]'
                            }`}
                            data-testid={`exam-present-btn-${l.learnerId}`}
                          >
                            {eduSeats[l.learnerId]?.present !== false ? t('NLSFieldPassSchoolStatusPresent') : t('NLSFieldPassSchoolStatusAbsent')}
                          </Pressable>
                        </div>
                      </div>
                    ))}
                    {learners.length === 0 ? (
                      <div className="p-4 text-center text-xs text-[var(--muted)]">{t('NLSFieldPassSchoolEmptyAssigned')}</div>
                    ) : null}
                  </div>

                  <Button
                    variant="primary"
                    className="w-full"
                    disabled={Boolean(eduSubmitBusy || isExpired)}
                    onClick={submitExamSeats}
                    data-testid="submit-exam-seats-btn"
                  >
                    {eduSubmitBusy ? t('NLSFieldPassSchoolSubmitting') : t('NLSFieldPassSchoolSubmitExamSeats')}
                  </Button>
                </div>
              ) : null}

              {eduScope?.kind === 'COACH' ? (
                <div className="space-y-4" data-testid="field-pass-coach-mode">
                  <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-card)] p-4">
                    <h2 className="text-base font-semibold">{snapshot.pass.workspaceName}</h2>
                    <p className="text-xs text-[var(--muted)]">{t('NLSFieldPassSchoolHeadingCoach')}</p>
                  </div>

                  <div className="space-y-3" data-testid="school-pass-coach-metrics">
                    {learners.map((l) => (
                      <div
                        key={l.learnerId}
                        className="rounded-xl border border-[var(--border)] bg-[var(--surface-card)] p-3 space-y-2"
                        data-testid={`coach-metric-row-${l.learnerId}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm">{l.displayName}</span>
                          <span className="text-xs text-[var(--muted)]">{t('NLSFieldPassSchoolRollNumber', { rollNo: l.rollNo ?? '—' })}</span>
                        </div>
                        {(eduScope.metricDefIds ?? ['score']).map((mId) => (
                          <div key={mId} className="flex items-center justify-between gap-2">
                            <label className="text-xs text-[var(--muted)] capitalize">{mId}:</label>
                            <TextInput
                              aria-label={t('NLSFieldPassSchoolAriaMetricFor', { metric: mId, name: l.displayName })}
                              value={eduMetrics[`${l.learnerId}_${mId}`]?.value !== undefined ? String(eduMetrics[`${l.learnerId}_${mId}`]?.value) : ''}
                              onChange={(val) => handleCoachMetricChange(l.learnerId, mId, val)}
                              className="w-28 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 py-1 text-xs"
                              placeholder={t('NLSFieldPassSchoolPlaceholderScoreValue')}
                              data-testid={`coach-metric-${l.learnerId}-${mId}`}
                            />
                          </div>
                        ))}
                        <TextInput
                          aria-label={t('NLSFieldPassSchoolAriaNotesFor', { name: l.displayName })}
                          value={eduMetrics[`${l.learnerId}_notes`]?.notes ?? ''}
                          onChange={(val) => handleCoachNotesChange(l.learnerId, val)}
                          className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 py-1 text-xs"
                          placeholder={t('NLSFieldPassSchoolPlaceholderCoachNotes')}
                          data-testid={`coach-notes-${l.learnerId}`}
                        />
                      </div>
                    ))}
                    {learners.length === 0 ? (
                      <div className="p-4 text-center text-xs text-[var(--muted)]">{t('NLSFieldPassSchoolEmptyAssigned')}</div>
                    ) : null}
                  </div>

                  <Button
                    variant="primary"
                    className="w-full"
                    disabled={Boolean(eduSubmitBusy || isExpired)}
                    onClick={submitCoachMetrics}
                    data-testid="submit-coach-metrics-btn"
                  >
                    {eduSubmitBusy ? t('NLSFieldPassSchoolSubmitting') : t('NLSFieldPassSchoolSubmitSessionMetrics')}
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </section>
      ) : (
        <>
          <section className="mx-auto grid max-w-3xl gap-4 px-4 py-4 pb-24">
        {(error || status) ? (
          <p className={`rounded-xl border px-3 py-2 text-xs ${error ? 'border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]' : 'border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[color-mix(in_srgb,var(--success)_10%,transparent)] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]'}`}>{error ?? status}</p>
        ) : null}

        <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-card)] p-4">
          <p className="text-xs text-[var(--muted)] break-words">{snapshot.pass.workspaceName}</p>
          <h2 className="mt-1 text-xl font-semibold">{completionCount}/{snapshot.tasks.length} assigned items complete</h2>
        </div>

        {tab === 'TODOS' ? (
          <div className="grid gap-3">
            {snapshot.tasks.map((task) => (
              <article key={task.id} className="rounded-2xl border border-[var(--border)] bg-[var(--surface-card)] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold break-words">{task.title}</h3>
                    <p className="mt-1 text-xs text-[var(--muted)]">{taskDate(task)}</p>
                  </div>
                  <span className="rounded-full bg-[var(--surface-raised)] px-2 py-1 text-[10px] uppercase tracking-widest text-[var(--text-strong)]">{statusLabel(task.workflowStatus)}</span>
                </div>
                {task.description ? <p className="mt-3 whitespace-pre-wrap break-words text-sm text-[var(--text)]">{task.description}</p> : null}
                {task.completionCriteria ? <p className="mt-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 text-xs break-words text-[var(--text)]">{task.completionCriteria}</p> : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((option) => (
                    <Pressable
                      key={option}
                      className="flex h-11 min-w-11 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface-raised)] px-4 text-xs font-medium text-[var(--text-strong)] disabled:opacity-60"
                      aria-pressed={task.workflowStatus === option}
                      disabled={busy === `status:${task.id}`}
                      onClick={() => void updateStatus(task.id, option)}
                    >
                      {statusLabel(option)}
                    </Pressable>
                  ))}
                </div>
                <label className="mt-3 grid gap-1 text-[11px] font-semibold uppercase tracking-widest text-[var(--muted)]">
                  Upload proof
                  <FilePicker
                    accept="image/*,video/*,audio/*,application/pdf"
                    maxBytes={8 * 1024 * 1024}
                    multiple={false}
                    disabled={busy === `proof:${task.id}`}
                    onFiles={(files) => void uploadProof(task.id, files[0])}
                    onReject={(rejects) =>
                      setError(
                        rejects.some((r) => r.reason === 'size')
                          ? 'File exceeds the 8 MB limit.'
                          : 'File type not accepted.',
                      )
                    }
                  />
                  {task.proofFileName ? <span className="text-xs normal-case tracking-normal break-words text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]">Current proof: {task.proofFileName}</span> : null}
                </label>
                <div className="mt-3 grid gap-2">
                  <Textarea value={commentDrafts[task.id] ?? ''} onChange={(val) => setCommentDrafts((current) => ({ ...current, [task.id]: val }))} placeholder="Add a task comment..." />
                  <Button variant="secondary" disabled={busy === `comment:${task.id}` || !commentDrafts[task.id]?.trim()} onClick={() => void addComment(task.id)}>Add Comment</Button>
                </div>
              </article>
            ))}
          </div>
        ) : null}

        {tab === 'CHAT' ? (
          <section className="grid min-h-[60dvh] grid-rows-[1fr_auto] rounded-2xl border border-[var(--border)] bg-[var(--surface-card)]">
            <div className="grid content-end gap-3 overflow-y-auto p-4">
              {messages.length === 0 ? <p className="text-center text-sm text-[var(--muted)]">Send the first message to open a private Field Pass chat with the member who created this pass.</p> : null}
              {messages.map((message) => (
                <div key={message.id} className={`max-w-[82%] rounded-2xl px-3 py-2 ${message.authorRole === 'BURNER' ? 'ml-auto bg-[var(--accent-soft)] text-[var(--accent-strong)]' : 'bg-[var(--surface-raised)] text-[var(--text-strong)]'}`}>
                  <p className="text-[10px] uppercase tracking-widest text-[var(--muted)]">{message.authorName} · {message.authorRole === 'BURNER' ? 'Burner' : 'Member'} {formatTime(message.timestamp)}</p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm">{message.textContent}</p>
                </div>
              ))}
            </div>
            <div
              className="sticky bottom-0 border-t border-[var(--border)] bg-[var(--surface)]/95 p-3 backdrop-blur"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
            >
              <form onSubmit={sendChat} className="flex gap-2">
                <div className="min-w-0 flex-1">
                  <TextInput value={chatDraft} onChange={setChatDraft} placeholder="Message..." />
                </div>
                <IconButton
                  variant="primary"
                  size="lg"
                  type="submit"
                  label="Send"
                  icon={<SendRoundedIcon fontSize="small" />}
                  disabled={busy === 'chat' || !chatDraft.trim()}
                />
              </form>
            </div>
          </section>
        ) : null}

        {tab === 'CONTROLS' ? (
          <section className="grid gap-3 rounded-2xl border border-[var(--border)] bg-[var(--surface-card)] p-4">
            <h2 className="text-lg font-semibold">Controls</h2>
            <form onSubmit={submitScan} className="grid gap-3">
              <SheetSelect
                ariaLabel="Task to scan"
                value={scanTaskId}
                options={snapshot.tasks.map((task) => ({ value: task.id, label: task.title }))}
                onChange={setScanTaskId}
              />
              <div className="grid grid-cols-2 gap-2">
                <Pressable aria-pressed={scanMode === 'OUT'} onClick={() => setScanMode('OUT')} className={`h-11 rounded-xl border text-sm font-medium ${scanMode === 'OUT' ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : 'border-[var(--border)] bg-[var(--surface-raised)]'}`}>Scan Out</Pressable>
                <Pressable aria-pressed={scanMode === 'IN'} onClick={() => setScanMode('IN')} className={`h-11 rounded-xl border text-sm font-medium ${scanMode === 'IN' ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : 'border-[var(--border)] bg-[var(--surface-raised)]'}`}>Scan In</Pressable>
              </div>
              <TextInput value={scanDraft} onChange={setScanDraft} placeholder="Barcode or inventory code" />
              <Button variant="primary" disabled={busy === 'scan' || !scanDraft.trim()}>Record Scan</Button>
            </form>
          </section>
        ) : null}
      </section>

          <nav
            className="sticky bottom-0 z-20 border-t border-[var(--border)] bg-[var(--surface)]/95 p-3 backdrop-blur"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
          >
            <div className="mx-auto grid max-w-3xl grid-cols-3 gap-2">
              {(['TODOS', 'CHAT', 'CONTROLS'] as TabKey[]).map((key) => (
                <Pressable key={key} aria-pressed={tab === key} onClick={() => setTab(key)} className={`h-11 rounded-xl text-xs font-semibold uppercase tracking-widest ${tab === key ? 'bg-[var(--btn-primary-from)] text-[var(--on-accent)]' : 'bg-[var(--surface-raised)] text-[var(--text)]'}`}>
                  {key === 'TODOS' ? 'Todos' : key === 'CHAT' ? 'Chat' : 'Controls'}
                </Pressable>
              ))}
            </div>
          </nav>
        </>
      )}
    </main>
  )
}

export default FieldPassLanding
